import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useSelector } from 'react-redux';

import { store } from 'redux/store';
import { resetCP, selectCPMFTrackingForecastSubpage } from 'redux/reducers/meteorologyFieldsTrackingForecastControlPanelReducer';
import { SUBPAGE_NAMES } from 'types/Meteorology/Fields/TrackingForecast/DetailedView';

import useControls from '../useControls';
import useChartRequestTrigger from '../useChartRequestTrigger';
import useSavePreference from '../useSavePreference';
import useSelectPreference from '../useSelectPreference';
import useListPreferences from '../useListPreferences';
import useDeletePreference from '../useDeletePreference';
import useGroupedLocations from '../useGroupedLocations';
import useTimeFrequency from '../useTimeFrequency';
import useModels from '../useModel';
import useVariables from '../useVariables';
import useMembers from '../useMembers';
import useForecastDate from '../useForecastDate';
import useRounds from '../useRounds';

export default function useDetailedViewMobile() {
  const subpage = useSelector(selectCPMFTrackingForecastSubpage);
  const { subpageButtonProps, chartMode, setChartMode } = useControls();
  const triggerRequest = useChartRequestTrigger();
  const savePreference = useSavePreference();
  const { selectPreference } = useSelectPreference();
  const { preferences } = useListPreferences();
  const { preferenceDeleteTrigger } = useDeletePreference();
  const groupedLocations = useGroupedLocations();
  const timeFrequency = useTimeFrequency();
  const models = useModels();
  const variables = useVariables();
  const members = useMembers();
  const forecastDate = useForecastDate();
  const rounds = useRounds();

  const clearSelection = () => store.dispatch(resetCP());

  const variableLabel = variables.variables.find(
    (v) => v.value === variables.selectedVariable,
  )?.label;
  const dateLabel = forecastDate.selectedDate
    ? format(forecastDate.selectedDate, 'dd/MM/yyyy', { locale: ptBR })
    : undefined;
  const selectedMultiModelCount = models.models.filter(
    (m) => models.isModelSelected(m.value),
  ).length;

  const chips: string[] = [];
  if (timeFrequency.selectedTimeFrequency) chips.push(timeFrequency.selectedTimeFrequency.label);
  if (variableLabel) chips.push(variableLabel);
  if (subpage === 'locations') {
    if (models.selectedModel) chips.push(models.selectedModel.label);
    if (groupedLocations.selectedMultiLocations.length) {
      chips.push(`${groupedLocations.selectedMultiLocations.length} locais`);
    }
    if (dateLabel) chips.push(dateLabel);
  } else if (subpage === 'members') {
    if (groupedLocations.selectedLocation) chips.push(groupedLocations.selectedLocation.name);
    if (models.selectedModel) chips.push(models.selectedModel.label);
    if (members.selectedMembers.length) chips.push(`${members.selectedMembers.length} membros`);
    if (dateLabel) chips.push(dateLabel);
  } else if (subpage === 'models') {
    if (groupedLocations.selectedLocation) chips.push(groupedLocations.selectedLocation.name);
    if (selectedMultiModelCount) chips.push(`${selectedMultiModelCount} modelos`);
    if (dateLabel) chips.push(dateLabel);
  } else if (subpage === 'rounds') {
    if (groupedLocations.selectedLocation) chips.push(groupedLocations.selectedLocation.name);
    if (models.selectedModel) chips.push(models.selectedModel.label);
    if (rounds.selectedRounds.length) chips.push(`${rounds.selectedRounds.length} rodadas`);
  }

  return {
    subpage,
    subpageLabel: SUBPAGE_NAMES[subpage],
    subpageButtonProps,
    chartMode,
    setChartMode,
    chips: chips.slice(0, 4),
    triggerRequest,
    clearSelection,
    savePreference,
    selectPreference,
    preferences,
    preferenceDeleteTrigger,
    groupedLocations,
    timeFrequency,
    models,
    variables,
    members,
    forecastDate,
    rounds,
  };
}
